import { RobotOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { BasketballTeamJudgeGame, UserBasic } from '@hefe/models';
import {
  BasketballGamesSlice,
  BasketballTeamSlice,
  usePrevious,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import { Button, message, Modal, Select, Tooltip } from 'antd';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import TeamJudgesAutoAssignmentComponent from '../../components/team-judges-auto-assignment/team-judges-auto-assignment.component';

export default function TeamJudgesPage() {
  const dispatch = useAppDispatch();
  const activeCoachId = useSelector(userActiveBasketballCoachTeamId);
  const prevActiveCoachId = usePrevious(activeCoachId);
  const games = useSelector(
    BasketballTeamSlice.basketballTeamJudgeGamesSelector
  );
  const playersSelect = useSelector(
    BasketballTeamSlice.basketballTeamPlayersSelectWithPendingSelector
  );
  const previewData = useSelector(
    BasketballTeamSlice.basketballTeamJudgeGamesAutoAssignmentPreviewSelector
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<{ [key: number]: number[] }>({});
  const [modalOpen, setModalOpen] = useState(-1);
  const [savingAutoAssign, setSavingAutoAssign] = useState(false);

  useEffect(() => {
    fetchGames();
    fetchPlayersSelect();
  }, []);

  useEffect(() => {
    if (
      prevActiveCoachId !== undefined &&
      activeCoachId !== prevActiveCoachId
    ) {
      fetchGames();
      fetchPlayersSelect();
    }
  }, [activeCoachId]);

  const fetchGames = () => {
    if (!activeCoachId) {
      return;
    }

    setLoading(true);
    dispatch(BasketballTeamSlice.fetchBasketballTeamJudgeGames(activeCoachId))
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  };

  const fetchPlayersSelect = () => {
    if (!activeCoachId) {
      return;
    }

    dispatch(
      BasketballTeamSlice.fetchBasketballTeamPlayersSelect({
        teamId: activeCoachId,
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const updateJudge = (
    gameId: number,
    value: number | undefined,
    judge: number
  ) => {
    setSelectFieldSaving(true, gameId, judge);
    dispatch(
      BasketballGamesSlice.setBasketballGameJudgeUser({
        gameId: gameId,
        data: {
          judge: judge,
          user_id: value,
        },
      })
    )
      .unwrap()
      .catch((error) => {
        if (error.response?.status === 409) {
          message.error(t('errors.update_judge_user.already_exists'));
        }
      })
      .finally(() => {
        setSelectFieldSaving(false, gameId, judge);
      });
  };

  const autoAssignJudges = () => {
    setSavingAutoAssign(true);
    dispatch(
      BasketballTeamSlice.createBasketballTeamJudgeGamesAutoAssignment(
        previewData
      )
    )
      .unwrap()
      .then(() => setModalOpen(-1))
      .finally(() => setSavingAutoAssign(false));
  };

  const setSelectFieldSaving = (
    save: boolean,
    gameId: number,
    judge: number
  ) => {
    setSaving((state) =>
      produce(state, (draft) => {
        if (!draft[gameId]) {
          draft[gameId] = [];
        }

        if (save) {
          draft[gameId].push(judge);
        } else {
          draft[gameId] = draft[gameId].filter((num) => num !== judge);
        }
      })
    );
  };

  const renderPlayerSelect = (
    user: UserBasic | undefined,
    judge: number,
    gameId: number
  ) => {
    const isSaving = saving[gameId]?.includes(judge);
    return (
      <Select
        style={{ width: '100%' }}
        options={playersSelect}
        value={user?.id ?? null}
        onChange={(value) => updateJudge(gameId, value, judge)}
        loading={isSaving}
      />
    );
  };

  return (
    <div>
      <CustomTable
        name="team-judges"
        rowKey={'id'}
        loading={loading}
        dataSource={games}
        columns={[
          {
            key: 'date',
            title: t('date'),
            dataIndex: 'date',
            render: (value) => formatDateTime(value),
            showDateFilter: true,
            default: true,
          },
          {
            key: 'game',
            title: t('games.singular'),
            render: (value) => `${value.home} - ${value.guest}`,
            showFilter: true,
            default: true,
          },
          {
            key: 'judge_person_1',
            title: t('person') + ' 1',
            dataIndex: 'judge_paper',
            default: true,
            render: (
              value: UserBasic | undefined,
              game: BasketballTeamJudgeGame
            ) => renderPlayerSelect(value, 1, game.game_id),
          },
          {
            key: 'judge_person_2',
            title: t('person') + ' 2',
            dataIndex: 'judge_time',
            default: true,
            render: (
              value: UserBasic | undefined,
              game: BasketballTeamJudgeGame
            ) => renderPlayerSelect(value, 2, game.game_id),
          },
          {
            key: 'judge_person_3',
            title: t('person') + ' 3',
            dataIndex: 'judge_24sec',
            default: true,
            render: (
              value: UserBasic | undefined,
              game: BasketballTeamJudgeGame
            ) => renderPlayerSelect(value, 3, game.game_id),
          },
          {
            key: 'judge_person_4',
            title: t('person') + ' 4',
            dataIndex: 'judge_paper_asst',
            default: true,
            render: (
              value: UserBasic | undefined,
              game: BasketballTeamJudgeGame
            ) => renderPlayerSelect(value, 4, game.game_id),
          },
        ]}
        toolbarItems={[
          <Button.Group>
            <Tooltip
              title={t('judges.auto_assignment.title_with_number', {
                number: 3,
              })}
            >
              <Button icon={<RobotOutlined />} onClick={() => setModalOpen(3)}>
                3
              </Button>
            </Tooltip>
            <Tooltip
              title={t('judges.auto_assignment.title_with_number', {
                number: 4,
              })}
            >
              <Button icon={<RobotOutlined />} onClick={() => setModalOpen(4)}>
                4
              </Button>
            </Tooltip>
          </Button.Group>,
        ]}
      />
      <Modal
        open={modalOpen > 0}
        onCancel={() => setModalOpen(-1)}
        onOk={() => autoAssignJudges()}
        confirmLoading={savingAutoAssign}
        width={1000}
      >
        <TeamJudgesAutoAssignmentComponent
          numberOfUsers={modalOpen === 3 ? 3 : 4}
        />
      </Modal>
    </div>
  );
}
